import React, { useState, useEffect, useMemo } from 'react';
import { Clock, CheckCircle, AlertCircle, Loader2, Plus, Calendar, Briefcase, X, Info, Download, Settings, Shield, Upload, Sparkles, Camera, ChevronRight, ChevronLeft } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { parseISO, addDays, format, startOfWeek } from 'date-fns';
import { useAuth } from '../../context/AuthContext';
import { generateAbsencePdf } from '../../lib/absencePdfGenerator';

const api = (path: string, options?: RequestInit) => {
    const token = localStorage.getItem('token');
    return fetch(`/api${path}`, {
        ...options,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...options?.headers }
    }).then(r => r.json());
};

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
    pending: { label: 'En attente', color: 'bg-warning-500/15 text-warning-400 border-warning-500/20' },
    accepted: { label: 'Acceptée', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' },
    approved: { label: 'Acceptée', color: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20' },
    refused: { label: 'Refusée', color: 'bg-red-500/15 text-red-400 border-red-500/20' },
};

const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    // Handle ISO format yyyy-MM-dd or yyyy-MM-ddTHH:mm:ss
    const parts = dateStr.split('T')[0].split('-');
    if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
    return dateStr;
};

interface AbsenceRequest {
    id: string;
    type_code: string;
    date_start: string;
    date_end: string;
    note: string;
    status: string;
    created_at: string;
    days_count?: number;
    hire_date?: string;
    updated_at?: string;
}

type Tab = 'requests' | 'settings';

export const EmployeeAbsences: React.FC = () => {
    const { user, refreshUser } = useAuth();
    const [activeTab, setActiveTab] = useState<Tab>('requests');
    const [requests, setRequests] = useState<AbsenceRequest[]>([]);
    const [allowedCodes, setAllowedCodes] = useState<string[]>([]);
    const [empDefaults, setEmpDefaults] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(true);
    const [showInactiveModal, setShowInactiveModal] = useState(false);
    const [form, setForm] = useState({ type_code: '', date_start: '', date_end: '', note: '', hire_date: '' });
    const [rhSettings, setRhSettings] = useState<any>(null);
    const [submitting, setSubmitting] = useState(false);
    const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
    const signatureInputRef = React.useRef<HTMLInputElement>(null);
    const [signatureUrl, setSignatureUrl] = useState('');
    const [signatureSaving, setSignatureSaving] = useState(false);

    useEffect(() => {
        if (user?.portal_signature_url) setSignatureUrl(user.portal_signature_url);
    }, [user?.portal_signature_url]);

    // Digitize Signature Modal state
    const [showDigitizeModal, setShowDigitizeModal] = useState(false);
    const [digitizeStep, setDigitizeStep] = useState(1);
    const [digitizePhoto, setDigitizePhoto] = useState<string | null>(null);
    const [digitizeSubmitting, setDigitizeSubmitting] = useState(false);
    const cameraInputRef = React.useRef<HTMLInputElement>(null);
    const digitizeFileInputRef = React.useRef<HTMLInputElement>(null);

    const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const loadAbsencesData = async () => {
        setLoading(true);
        try {
            const [reqs, rhS, planS] = await Promise.all([
                api('/absence-requests'),
                api('/rh/settings'),
                api('/planning/settings').catch(() => ({}))
            ]);
            setRequests(Array.isArray(reqs) ? reqs : []);
            setAllowedCodes(Array.isArray(rhS?.rh_allowed_absence_codes) ? rhS.rh_allowed_absence_codes : []);
            if (rhS && !rhS.error) setRhSettings(rhS);
            
            if (user?.id && planS?.weeklyDefaults?.[user.id]) {
                setEmpDefaults(planS.weeklyDefaults[user.id]);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAbsencesData();
    }, []);

    useEffect(() => {
        if (user?.hire_date) {
            setForm(f => ({ ...f, hire_date: String(user.hire_date).slice(0, 10) }));
        }
    }, [user?.hire_date]);

    const [daysInfo, setDaysInfo] = useState({ totalDays: 0, restDays: 0, absenceDays: 0 });
    const [fetchedWeeks, setFetchedWeeks] = useState<Record<string, any>>({});
    const [isCalculatingDays, setIsCalculatingDays] = useState(false);

    useEffect(() => {
        const calculate = async () => {
            if (!form.date_start || !form.date_end) {
                setDaysInfo({ totalDays: 0, restDays: 0, absenceDays: 0 });
                return;
            }
            const start = parseISO(form.date_start);
            const end = parseISO(form.date_end);
            if (isNaN(start.getTime()) || isNaN(end.getTime()) || start > end) {
                setDaysInfo({ totalDays: 0, restDays: 0, absenceDays: 0 });
                return;
            }

            setIsCalculatingDays(true);

            let totalDays = 0;
            let restDays = 0;
            let absenceDays = 0;
            const dayKeys = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];

            // First, determine all the weeks we need to fetch
            const weeksNeeded = new Set<string>();
            let curr = new Date(start);
            while (curr <= end) {
                const monday = startOfWeek(curr, { weekStartsOn: 1 });
                weeksNeeded.add(format(monday, 'yyyy-MM-dd'));
                curr = addDays(curr, 1);
            }

            // Fetch missing weeks
            const newFetched = { ...fetchedWeeks };
            let hasNewFetches = false;
            for (const weekStart of Array.from(weeksNeeded)) {
                if (newFetched[weekStart] === undefined) {
                    try {
                        const weekData = await api(`/planning/week/${weekStart}`);
                        newFetched[weekStart] = weekData && !weekData.error ? weekData : null;
                    } catch {
                        newFetched[weekStart] = null;
                    }
                    hasNewFetches = true;
                }
            }
            
            if (hasNewFetches) {
                setFetchedWeeks(newFetched);
            }

            let current = new Date(start);
            while (current <= end) {
                totalDays++;
                const jsDay = current.getDay();
                const dayIndex = (jsDay + 6) % 7; // Monday = 0, Sunday = 6
                const dateStr = format(current, 'yyyy-MM-dd');
                
                const monday = startOfWeek(current, { weekStartsOn: 1 });
                const weekStartStr = format(monday, 'yyyy-MM-dd');
                
                const weekData = newFetched[weekStartStr];
                
                if (weekData && weekData.week_start === weekStartStr) {
                    const empRow = (weekData.rows || []).find((r: any) => String(r.employeeId) === String(user?.id));
                    const savedShift = empRow?.shifts?.[dateStr];
                    
                    if (savedShift && savedShift.type === 'travail' && Array.isArray(savedShift.segments) && savedShift.segments.length > 0) {
                        absenceDays++;
                    } else if (savedShift && savedShift.type === 'absence') {
                        absenceDays++;
                    } else {
                        restDays++;
                    }
                } else {
                    const shift = empDefaults[String(dayIndex)] || empDefaults[dayKeys[dayIndex]];
                    if (!shift || shift === 'REPOS') {
                        restDays++;
                    } else {
                        absenceDays++;
                    }
                }
                
                current = addDays(current, 1);
            }

            setDaysInfo({ totalDays, restDays, absenceDays });
            setIsCalculatingDays(false);
        };
        calculate();
    }, [form.date_start, form.date_end, empDefaults, user?.id]);

    const todayStr = format(new Date(), 'yyyy-MM-dd');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (allowedCodes.length === 0) {
            setShowInactiveModal(true);
            return;
        }
        if (!user?.portal_signature_url) {
            setActiveTab('settings');
            showToast('Configurez votre signature avant de soumettre une demande.', 'error');
            return;
        }
        if (!form.type_code || !form.date_start || !form.date_end) return;
        if (form.date_start < todayStr) {
            showToast('La date de début ne peut pas être dans le passé.', 'error');
            return;
        }
        setSubmitting(true);
        const payload = {
            ...form,
            days_count: daysInfo.absenceDays
        };
        const r = await api('/absence-requests', { method: 'POST', body: JSON.stringify(payload) });
        setSubmitting(false);
        if (r.error) showToast(r.error, 'error');
        else {
            showToast('Demande d\'absence envoyée avec succès !');
            setForm({ type_code: '', date_start: '', date_end: '', note: '', hire_date: user?.hire_date ? String(user.hire_date).slice(0, 10) : '' });
            loadAbsencesData();
            
            // Dispatch custom event to update the sidebar badge instantly
            window.dispatchEvent(new Event('absenceRequestProcessed'));
        }
    };

    const handleSignatureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (ev) => {
            const rawDataUrl = ev.target?.result as string;
            if (!rawDataUrl) return;

            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const maxW = 500;
                const maxH = 250;
                let width = img.width;
                let height = img.height;

                if (width > maxW) {
                    height = Math.round((height * maxW) / width);
                    width = maxW;
                }
                if (height > maxH) {
                    width = Math.round((width * maxH) / height);
                    height = maxH;
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (!ctx) return;
                ctx.clearRect(0, 0, width, height);
                ctx.drawImage(img, 0, 0, width, height);
                setSignatureUrl(canvas.toDataURL('image/png'));
            };
            img.src = rawDataUrl;
        };
        reader.readAsDataURL(file);
    };

    const saveSignature = async () => {
        if (!signatureUrl) return;
        setSignatureSaving(true);
        try {
            const r = await api('/employe/signature', { method: 'PUT', body: JSON.stringify({ signature_url: signatureUrl }) });
            if (r.error) showToast(r.error, 'error');
            else {
                showToast('Signature enregistrée avec succès.');
                await refreshUser();
            }
        } catch (err) {
            showToast('Erreur lors de l\'enregistrement de la signature', 'error');
        } finally {
            setSignatureSaving(false);
        }
    };

    const handleDigitizeFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => {
            const raw = ev.target?.result as string;
            if (!raw) return;

            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => {
                const canvas = document.createElement('canvas');
                const maxW = 1000;
                const maxH = 1000;
                let width = img.width;
                let height = img.height;

                if (width > maxW) {
                    height = Math.round((height * maxW) / width);
                    width = maxW;
                }
                if (height > maxH) {
                    width = Math.round((width * maxH) / height);
                    height = maxH;
                }

                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                if (ctx) {
                    ctx.drawImage(img, 0, 0, width, height);
                    setDigitizePhoto(canvas.toDataURL('image/jpeg', 0.85));
                }
            };
            img.src = raw;
        };
        reader.readAsDataURL(file);
    };

    const submitDigitizeRequest = async () => {
        if (!digitizePhoto) return;
        setDigitizeSubmitting(true);
        try {
            const r = await api('/employe/digitize-signature-request', {
                method: 'POST',
                body: JSON.stringify({ image_base64: digitizePhoto })
            });
            setDigitizeSubmitting(false);
            if (r.error) {
                showToast(r.error, 'error');
            } else {
                setDigitizeStep(3);
                showToast('Demande transmise avec succès !');
            }
        } catch (e) {
            setDigitizeSubmitting(false);
            showToast('Erreur lors de la transmission', 'error');
        }
    };

    return (
        <div className="p-4 md:p-8 min-h-screen dark:bg-surface space-y-6">
            {/* Toast */}
            <AnimatePresence>
                {toast && (
                    <motion.div
                        initial={{ opacity: 0, y: -20, x: '50%' }}
                        animate={{ opacity: 1, y: 0, x: '50%' }}
                        exit={{ opacity: 0, y: -20, x: '50%' }}
                        className={`fixed top-6 right-1/2 z-50 px-5 py-3 rounded-2xl font-bold text-sm flex items-center gap-2 shadow-2xl border ${toast.type === 'success' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30' : 'bg-red-500/20 text-red-300 border-red-500/30'}`}
                    >
                        {toast.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
                        {toast.msg}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Modal de demandes non activées */}
            <AnimatePresence>
                {showInactiveModal && (
                    <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4">
                        <motion.div 
                            initial={{ scale: 0.9, opacity: 0 }} 
                            animate={{ scale: 1, opacity: 1 }} 
                            exit={{ scale: 0.9, opacity: 0 }} 
                            className="bg-slate-900 border border-border rounded-3xl p-6 max-w-md w-full text-center space-y-4 shadow-2xl relative"
                        >
                            <button onClick={() => setShowInactiveModal(false)} className="absolute top-4 right-4 text-content-muted hover:text-content p-1">
                                <X size={18} />
                            </button>
                            <div className="w-14 h-14 rounded-2xl bg-warning-500/15 text-warning-400 mx-auto flex items-center justify-center">
                                <AlertCircle size={28} />
                            </div>
                            <h3 className="text-lg font-black text-content">Demandes non activées</h3>
                            <p className="text-sm text-content-muted leading-relaxed">
                                Les demandes d'absence ne sont pas encore activées dans votre établissement.<br/><br/>
                                Merci de vous rapprocher du responsable de votre établissement afin qu'il autorise les types d'absences dans ses paramètres RH.
                            </p>
                            <button onClick={() => setShowInactiveModal(false)} className="w-full bg-brand-600 hover:bg-brand-500 text-brand-fg font-bold py-3 rounded-xl transition-all shadow-lg shadow-brand-600/30">
                                Compris
                            </button>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* Header */}
            <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-brand-500/15 flex items-center justify-center text-brand-600 dark:text-brand-400">
                    <Clock size={24} />
                </div>
                <div>
                    <h1 className="text-2xl font-black text-content">Mes Demandes d'Absence</h1>
                    <p className="text-sm text-content-muted">Consultez l'historique et soumettez de nouvelles demandes d'absence.</p>
                </div>
            </div>

            <div className="bg-surface-soft rounded-2xl border border-border p-2 inline-flex gap-2">
                <button onClick={() => setActiveTab('requests')} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${activeTab === 'requests' ? 'bg-brand-600 text-brand-fg shadow-sm' : 'text-[var(--text-muted)] hover:bg-brand-600/10 hover:text-brand-600 dark:hover:text-brand-400'}`}>
                    <Calendar size={16} /> Demandes & Soldes
                </button>
                <button onClick={() => setActiveTab('settings')} className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all ${activeTab === 'settings' ? 'bg-brand-600 text-brand-fg shadow-sm' : 'text-[var(--text-muted)] hover:bg-brand-600/10 hover:text-brand-600 dark:hover:text-brand-400'}`}>
                    <Settings size={16} /> Paramètres
                </button>
            </div>

            {activeTab === 'requests' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
                {/* Formulaire de demande */}
                <div className="lg:col-span-1 bg-surface-soft border border-border rounded-3xl p-6 space-y-4">
                    <h2 className="text-lg font-bold text-content flex items-center gap-2">
                        <Plus size={18} className="text-brand-600 dark:text-brand-400" /> Nouvelle Demande
                    </h2>
                    {allowedCodes.length === 0 ? (
                        <div className="space-y-4">
                            <button 
                                onClick={() => setShowInactiveModal(true)}
                                className="w-full bg-brand-600/20 hover:bg-brand-600/30 text-brand-500 dark:text-brand-600 dark:text-brand-400 border border-brand-500/30 py-3 px-4 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2"
                            >
                                <Plus size={16} /> Nouvelle Demande
                            </button>
                            <div className="p-4 bg-warning-500/10 border border-warning-500/20 rounded-2xl text-xs text-warning-300 space-y-1.5">
                                <p className="font-bold flex items-center gap-1.5 text-warning-200">
                                    <AlertCircle size={14} className="shrink-0" /> Demandes d'absence non activées
                                </p>
                                <p className="text-warning-300/80 leading-relaxed">
                                    Les demandes d'absence ne sont pas encore activées. Merci de vous tourner vers le responsable de l'établissement.
                                </p>
                            </div>
                        </div>
                    ) : !user?.portal_signature_url ? (
                        <div className="space-y-4">
                            <div className="p-4 bg-warning-500/10 border border-warning-500/20 rounded-2xl text-xs text-warning-300 space-y-1.5">
                                <p className="font-bold flex items-center gap-1.5 text-warning-200">
                                    <Shield size={14} className="shrink-0" /> Signature requise
                                </p>
                                <p className="text-warning-300/80 leading-relaxed">
                                    Vous devez configurer votre signature avant de pouvoir soumettre une demande d'absence.
                                </p>
                            </div>
                            <button
                                onClick={() => setActiveTab('settings')}
                                className="w-full bg-brand-600 hover:bg-brand-500 text-brand-fg py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2"
                            >
                                <Settings size={16} /> Configurer ma signature
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="text-xs text-content-muted font-semibold mb-1 block">Type d'absence</label>
                                <select 
                                    value={form.type_code} 
                                    onChange={e => setForm(f => ({ ...f, type_code: e.target.value }))}
                                    required
                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-content text-sm outline-none focus:border-brand-500/50"
                                >
                                    <option value="">-- Choisir --</option>
                                    {allowedCodes.map(code => <option key={code} value={code}>{code}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="text-xs text-content-muted font-semibold mb-1 block">Date de début (inclus)</label>
                                <input 
                                    type="date" 
                                    min={todayStr}
                                    value={form.date_start} 
                                    onChange={e => setForm(f => ({ ...f, date_start: e.target.value }))}
                                    required
                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-content text-sm outline-none focus:border-brand-500/50" 
                                />
                            </div>
                            <div>
                                <label className="text-xs text-content-muted font-semibold mb-1 block">Date de fin (inclus)</label>
                                <input 
                                    type="date" 
                                    min={form.date_start || todayStr}
                                    value={form.date_end} 
                                    onChange={e => setForm(f => ({ ...f, date_end: e.target.value }))}
                                    required
                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-content text-sm outline-none focus:border-brand-500/50" 
                                />
                            </div>

                            {/* Summary calculation */}
                            {daysInfo.totalDays > 0 && (
                                <div className="p-3 bg-brand-500/10 border border-brand-500/20 rounded-xl text-xs space-y-1.5">
                                    <div className="flex justify-between text-content-muted font-medium">
                                        <span>Période totale :</span>
                                        <span className="font-bold text-content">{daysInfo.totalDays} jour{daysInfo.totalDays > 1 ? 's' : ''} (inclus)</span>
                                    </div>
                                    {daysInfo.restDays > 0 && (
                                        <div className="flex justify-between text-warning-300/90">
                                            <span>Jours de repos déduits :</span>
                                            <span className="font-bold">-{daysInfo.restDays} jour{daysInfo.restDays > 1 ? 's' : ''}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-emerald-400 font-bold border-t border-border pt-1.5 mt-1.5 text-xs">
                                        <span>Jours d'absence comptabilisés :</span>
                                        <span className="text-sm">{daysInfo.absenceDays} jour{daysInfo.absenceDays > 1 ? 's' : ''}</span>
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="text-xs text-content-muted font-semibold mb-1 block">Message / Motif (optionnel)</label>
                                <textarea 
                                    value={form.note} 
                                    onChange={e => setForm(f => ({ ...f, note: e.target.value }))}
                                    placeholder="Ajouter un message ou un motif..."
                                    rows={3}
                                    className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-content text-sm outline-none focus:border-brand-500/50 resize-none text-left" 
                                />
                            </div>
                            {user?.hire_date ? (
                                <div className="text-xs text-content-muted bg-surface-soft border border-border rounded-xl px-3 py-2.5">
                                    Date d'ancienneté enregistrée : <strong className="text-content">{formatDate(user.hire_date)}</strong> — renseignée par votre employeur.
                                </div>
                            ) : (
                                <div>
                                    <label className="text-xs text-content-muted font-semibold mb-1 block">Date d'ancienneté <span className="text-content-muted">(optionnel — pour le document officiel)</span></label>
                                    <input
                                        type="date"
                                        value={form.hire_date}
                                        onChange={e => setForm(f => ({ ...f, hire_date: e.target.value }))}
                                        className="w-full bg-surface-soft border border-border rounded-xl px-3 py-2.5 text-content text-sm outline-none focus:border-brand-500/50"
                                    />
                                </div>
                            )}
                            <button 
                                type="submit" 
                                disabled={submitting}
                                className="w-full bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-brand-fg py-3 rounded-xl text-sm font-bold transition-all flex items-center justify-center gap-2"
                            >
                                {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                                Soumettre la demande
                            </button>
                        </form>
                    )}
                </div>

                {/* Liste des demandes */}
                <div className="lg:col-span-2 space-y-3">
                    <h2 className="text-lg font-bold text-content flex items-center gap-2 mb-4">
                        <Briefcase size={18} className="text-brand-600 dark:text-brand-400" /> Historique de mes demandes
                    </h2>

                    {loading ? (
                        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-brand-600 dark:text-brand-400" size={24} /></div>
                    ) : requests.length === 0 ? (
                        <div className="text-center py-16 border border-dashed border-border rounded-3xl bg-surface-soft">
                            <Calendar size={40} className="mx-auto mb-3 opacity-20" />
                            <p className="text-content-muted text-sm">Aucune demande enregistrée</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {requests.map(req => {
                                const s = STATUS_LABELS[req.status] || STATUS_LABELS.pending;
                                return (
                                    <motion.div key={req.id} layout className="p-5 bg-surface-soft rounded-2xl border border-border flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        <div>
                                            <div className="flex items-center gap-2 mb-2">
                                                <span className="font-bold text-brand-600 dark:text-brand-400 text-sm">{req.type_code}</span>
                                                <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded-lg border ${s.color}`}>{s.label}</span>
                                            </div>
                                            <p className="text-sm text-content-muted">
                                                Du <span className="text-content font-semibold">{formatDate(req.date_start)}</span> au <span className="text-content font-semibold">{formatDate(req.date_end)}</span>
                                            </p>
                                            {req.note && <p className="text-xs text-content-muted mt-2 italic">"{req.note}"</p>}
                                        </div>
                                        <div className="flex flex-col md:items-end gap-2">
                                            {(req.status === 'accepted' || req.status === 'approved') && (
                                                <button
                                                    onClick={() => generateAbsencePdf({
                                                        id: req.id,
                                                        employeeName: user?.name || 'Salarié',
                                                        employeePosition: (req as any).position,
                                                        employeeHireDate: req.hire_date,
                                                        companyName: rhSettings?.company_name || user?.company_name || 'Votre Établissement',
                                                        companyLogoUrl: rhSettings?.company_logo_url,
                                                        typeCode: req.type_code,
                                                        dateStart: req.date_start,
                                                        dateEnd: req.date_end,
                                                        daysCount: req.days_count,
                                                        note: req.note,
                                                        status: 'accepted',
                                                        createdAt: req.created_at,
                                                        approvedAt: req.updated_at || req.created_at,
                                                        signatoryName: rhSettings?.rh_signatory_name,
                                                        signatureUrl: rhSettings?.rh_signature_url,
                                                        employeeSignatureUrl: user?.portal_signature_url || undefined,
                                                        legal: rhSettings?.legal,
                                                        brandColor: user?.theme_color || undefined
                                                    })}
                                                    className="flex items-center gap-1.5 bg-brand-500/10 hover:bg-brand-500/10 text-brand-600 dark:text-brand-400 dark:text-brand-500 dark:text-brand-600 dark:text-brand-400 border border-brand-500/30 text-xs font-bold px-3 py-2 rounded-xl transition-all"
                                                >
                                                    <Download size={14} /> Télécharger PDF
                                                </button>
                                            )}
                                            <div className="text-xs text-content-muted whitespace-nowrap">
                                                Soumise le {new Date(req.created_at).toLocaleDateString('fr-FR')}
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>
            )}

            {activeTab === 'settings' && (
                <div className="max-w-xl">
                    <section className="bg-surface-soft rounded-3xl border border-border p-6 space-y-4">
                        <h3 className="font-bold text-content mb-1 flex items-center gap-2"><Shield size={16} className="text-brand-600 dark:text-brand-400" /> Ma signature</h3>
                        <p className="text-xs text-content-muted">Apparaît sur le document officiel d'autorisation d'absence. Une signature configurée est nécessaire pour soumettre une demande.</p>

                        <div>
                            <label className="text-xs text-content-muted font-semibold mb-1.5 block">Image de signature (fond blanc)</label>
                            {signatureUrl && (
                                <div className="mb-3 p-3 bg-white rounded-xl">
                                    <img src={signatureUrl} alt="Ma signature" className="max-h-20 object-contain" />
                                </div>
                            )}
                            <input ref={signatureInputRef} type="file" accept="image/*" className="hidden" onChange={handleSignatureUpload} />
                            <button onClick={() => signatureInputRef.current?.click()}
                                className="flex items-center gap-2 w-full bg-surface-soft hover:bg-slate-200 dark:bg-surface-soft border border-dashed border-slate-300 dark:border-border hover:border-brand-500/40 text-content-muted hover:text-content py-3 rounded-xl text-sm font-semibold transition-all justify-center">
                                <Upload size={16} /> {signatureUrl ? 'Changer la signature' : 'Uploader une image'}
                            </button>
                        </div>

                        <button onClick={() => { setShowDigitizeModal(true); setDigitizeStep(1); setDigitizePhoto(null); }}
                            className="flex items-center gap-2 w-full bg-brand-500/10 hover:bg-brand-500/20 border border-brand-500/20 hover:border-brand-500/40 text-brand-500 dark:text-brand-600 dark:text-brand-400 hover:text-brand-200 py-3 rounded-xl text-sm font-semibold transition-all justify-center">
                            <Sparkles size={16} /> Soumettre une demande pour numériser ma signature
                        </button>

                        <button onClick={saveSignature} disabled={signatureSaving || !signatureUrl}
                            className="w-full flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-brand-fg font-bold py-3 rounded-xl transition-all">
                            {signatureSaving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                            Enregistrer
                        </button>
                    </section>
                </div>
            )}

            {/* Modal - Numériser la signature manuscrite */}
            <AnimatePresence>
                {showDigitizeModal && (
                    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-slate-900 border border-border rounded-3xl p-6 max-w-lg w-full shadow-2xl space-y-5">
                            <div className="flex items-center justify-between border-b border-border pb-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-2xl bg-brand-500/10 border border-brand-500/20 flex items-center justify-center text-brand-600 dark:text-brand-400">
                                        <Sparkles size={20} />
                                    </div>
                                    <h3 className="text-lg font-bold text-content">Numériser ma signature</h3>
                                </div>
                                <button onClick={() => setShowDigitizeModal(false)} className="p-2 text-content-muted hover:text-content rounded-xl hover:bg-surface-soft transition-all"><X size={20} /></button>
                            </div>

                            {digitizeStep === 1 && (
                                <div className="space-y-4">
                                    <p className="text-sm text-content-muted">Pour obtenir une signature numérisée de qualité professionnelle, merci de suivre ces étapes :</p>
                                    <ul className="space-y-3 text-sm text-content-muted">
                                        <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-brand-500/15 text-brand-500 dark:text-brand-600 dark:text-brand-400 text-xs font-bold flex items-center justify-center shrink-0">1</span> <span>Prenez une feuille de papier <b className="text-content">blanche et vierge</b>.</span></li>
                                        <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-brand-500/15 text-brand-500 dark:text-brand-600 dark:text-brand-400 text-xs font-bold flex items-center justify-center shrink-0">2</span> <span>Signez au <b className="text-content">stylo noir</b>, bien lisiblement.</span></li>
                                        <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-brand-500/15 text-brand-500 dark:text-brand-600 dark:text-brand-400 text-xs font-bold flex items-center justify-center shrink-0">3</span> <span>Prenez la photo <b className="text-content">avec le flash</b>, en cadrant bien la signature : ni trop loin, ni trop près.</span></li>
                                    </ul>
                                    <button onClick={() => setDigitizeStep(2)} className="w-full flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-500 text-brand-fg font-bold py-3 rounded-xl transition-all">
                                        Suivant <ChevronRight size={16} />
                                    </button>
                                </div>
                            )}

                            {digitizeStep === 2 && (
                                <div className="space-y-4">
                                    {!digitizePhoto ? (
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleDigitizeFileChange} />
                                            <button onClick={() => cameraInputRef.current?.click()} className="flex flex-col items-center gap-2 bg-surface-soft hover:bg-slate-200 dark:bg-surface-soft border border-dashed border-slate-300 dark:border-border hover:border-brand-500/40 text-content-muted hover:text-content py-6 rounded-xl text-sm font-semibold transition-all">
                                                <Camera size={22} /> Appareil photo
                                            </button>
                                            <input ref={digitizeFileInputRef} type="file" accept="image/*" className="hidden" onChange={handleDigitizeFileChange} />
                                            <button onClick={() => digitizeFileInputRef.current?.click()} className="flex flex-col items-center gap-2 bg-surface-soft hover:bg-slate-200 dark:bg-surface-soft border border-dashed border-slate-300 dark:border-border hover:border-brand-500/40 text-content-muted hover:text-content py-6 rounded-xl text-sm font-semibold transition-all">
                                                <Upload size={22} /> Charger une image
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            <div className="p-3 bg-white rounded-xl">
                                                <img src={digitizePhoto} alt="Aperçu signature" className="max-h-40 w-full object-contain" />
                                            </div>
                                            <button onClick={() => setDigitizePhoto(null)} className="w-full text-xs text-content-muted hover:text-content transition-all">Reprendre une autre photo</button>
                                        </div>
                                    )}

                                    <div className="flex gap-3">
                                        <button onClick={() => setDigitizeStep(1)} className="flex-1 flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-700 text-white font-bold py-3 rounded-xl transition-colors shadow-sm">
                                            <ChevronLeft size={16} /> Retour
                                        </button>
                                        <button onClick={submitDigitizeRequest} disabled={!digitizePhoto || digitizeSubmitting}
                                            className="flex-1 flex items-center justify-center gap-2 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-brand-fg font-bold py-3 rounded-xl transition-all">
                                            {digitizeSubmitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                                            Soumettre ma demande
                                        </button>
                                    </div>
                                </div>
                            )}

                            {digitizeStep === 3 && (
                                <div className="space-y-4 text-center py-4">
                                    <div className="w-14 h-14 mx-auto rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center text-emerald-400">
                                        <CheckCircle size={28} />
                                    </div>
                                    <p className="text-content font-bold">Demande envoyée !</p>
                                    <p className="text-sm text-content-muted">Votre demande de numérisation a bien été transmise au service concerné. Vous recevrez votre signature numérisée sous peu.</p>
                                    <button onClick={() => setShowDigitizeModal(false)} className="w-full bg-brand-600 hover:bg-brand-500 text-brand-fg font-bold py-3 rounded-xl transition-all">
                                        Fermer
                                    </button>
                                </div>
                            )}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};
